import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { layoutOptions } from '../layouts';
import { selectLayout, updateLayoutProps, selectLayoutInfo } from '../redux/editorSlice';
import { getExternalLayouts } from '../layouts/ExternalLayoutLoader';
import { fetchOwnedSkins, searchSkins } from '../services/skinApi';
import { getRegistrySkins } from '../utils/registrySkinsCache';
import SkinPurchaseModal from './SkinPurchaseModal';

const LayoutSelector = () => {
  const dispatch = useDispatch();
  const layoutInfo = useSelector(selectLayoutInfo);
  const { selectedLayout, layoutProps = {} } = layoutInfo;
  const [allLayouts, setAllLayouts] = useState([]);
  const [registryLayouts, setRegistryLayouts] = useState([]);
  const [isPurchaseModalOpen, setIsPurchaseModalOpen] = useState(false);
  const [loadingSkins, setLoadingSkins] = useState(false);
  
  // 스킨 레지스트리에서 소유한 스킨 로드 (캐싱 시스템 사용 - 즉시 응답)
  const loadRegistryLayouts = async () => {
    try {

      // 새로운 캐싱 시스템 사용 (즉시 응답 가능, 로딩 상태 불필요)
      const formattedRegistryLayouts = await getRegistrySkins();
      
      setRegistryLayouts(formattedRegistryLayouts);
      
    } catch (error) {
      console.error('💥 레지스트리 스킨 로드 실패:', error);
    }
  };

  // 내장 레이아웃과 외부 레이아웃을 병합
  useEffect(() => {
    const internalLayouts = layoutOptions;
    const externalLayouts = getExternalLayouts();
    
    // 외부 레이아웃을 layoutOptions 형식으로 변환
    const formattedExternalLayouts = externalLayouts.map(layout => ({
      id: layout.id,
      name: layout.name,
      description: layout.description,
      category: '외부',
      options: []
    }));
    
    setAllLayouts([...internalLayouts, ...formattedExternalLayouts]);
    
    // 스킨 레지스트리 로드
    loadRegistryLayouts();
  }, []);
  
  // 레이아웃 선택 핸들러
  const handleLayoutSelect = (layoutId) => {
    dispatch(selectLayout(layoutId));
  };
  
  // 레이아웃 속성 변경 핸들러
  const handlePropertyChange = (propertyName, value) => {
    dispatch(updateLayoutProps({
      [propertyName]: value
    }));
  };
  
  // 구매 성공 핸들러
  const handlePurchaseSuccess = () => {
    // 스킨 레지스트리 다시 로드
    loadRegistryLayouts();
  };

  // 전체 레이아웃 목록 (기본 + 외부 + 레지스트리)
  const combinedLayouts = [...allLayouts, ...registryLayouts];

  // 선택된 레이아웃 정보 가져오기
  const selectedLayoutInfo = combinedLayouts.find(layout => layout.id === selectedLayout);
  
  return (
    <div className="layout-selector">
      <h3 className="mb-3">레이아웃 선택</h3>
      
      {/* 레이아웃 선택 영역 */}
      <div className="layout-selection mb-4">
        <div className="d-flex justify-content-between align-items-center mb-2">
          <label htmlFor="layout-select" className="form-label mb-0">레이아웃 템플릿</label>
          <button 
            className="btn btn-outline-primary btn-sm"
            onClick={() => setIsPurchaseModalOpen(true)}
          >
            스킨 마켓
          </button>
        </div>
        
        <div className="form-group">
          <select 
            id="layout-select" 
            className="form-select"
            value={selectedLayout || ''}
            onChange={(e) => handleLayoutSelect(e.target.value)}
          >
            <option value="">레이아웃 선택...</option>
            
            {/* 기본 스킨 */}
            <optgroup label="기본 스킨">
              {allLayouts.filter(layout => layout.category !== '외부').map(layout => (
                <option key={layout.id} value={layout.id}>
                  {layout.name}
                </option>
              ))}
            </optgroup>
            
            {/* 레지스트리 스킨 */}
            {registryLayouts.length > 0 && (
              <optgroup label="레지스트리 스킨 (보유중)">
                {registryLayouts.map(layout => (
                  <option key={layout.id} value={layout.id}>
                    ✓ {layout.name}
                  </option>
                ))}
              </optgroup>
            )}
            
            {/* 외부 스킨 */}
            {allLayouts.filter(layout => layout.category === '외부').length > 0 && (
              <optgroup label="외부 스킨">
                {allLayouts.filter(layout => layout.category === '외부').map(layout => (
                  <option key={layout.id} value={layout.id}>
                    {layout.name}
                  </option>
                ))}
              </optgroup>
            )}
          </select>
          
        </div>
        
        {selectedLayoutInfo && (
          <div className="layout-description mt-2">
            <p className="text-muted">{selectedLayoutInfo.description}</p>
            {(selectedLayoutInfo.previewImage || selectedLayoutInfo.thumbnail) && (
              <div className="layout-preview mt-2">
                <img 
                  src={selectedLayoutInfo.previewImage || selectedLayoutInfo.thumbnail} 
                  alt={selectedLayoutInfo.name} 
                  className="img-fluid border"
                  style={{ maxHeight: '200px' }}
                />
              </div>
            )}
            {selectedLayoutInfo.category === '레지스트리' && (
              <div className="mt-2">
                <span className="badge bg-success me-2">
                  <i className="fas fa-check-circle me-1"></i>
                  보유중인 레지스트리 스킨
                </span>
                {selectedLayoutInfo.skinData?.latestVersion && (
                  <small className="text-muted">
                    v{selectedLayoutInfo.skinData.latestVersion.version}
                  </small>
                )}
              </div>
            )}
          </div>
        )}
      </div>
      
      {/* 레이아웃 속성 설정 영역 */}
      {selectedLayoutInfo && selectedLayoutInfo.options && (
        <div className="layout-properties">
          <h4 className="mb-3">레이아웃 속성</h4>
          
          {selectedLayoutInfo.options.map(option => (
            <div className="form-group mb-3" key={option.name}>
              <label className="form-label">{option.label}</label>
              
              {option.type === 'boolean' && (
                <div className="form-check form-switch">
                  <input
                    className="form-check-input"
                    type="checkbox"
                    id={`layout-${option.name}`}
                    checked={layoutProps[option.name] ?? option.default}
                    onChange={(e) => handlePropertyChange(option.name, e.target.checked)}
                  />
                  <label 
                    className="form-check-label" 
                    htmlFor={`layout-${option.name}`}
                  >
                    {layoutProps[option.name] ? '켜짐' : '꺼짐'}
                  </label>
                </div>
              )}
              
              {option.type === 'select' && (
                <select
                  className="form-select"
                  value={layoutProps[option.name] ?? option.default}
                  onChange={(e) => handlePropertyChange(option.name, e.target.value)}
                >
                  {option.options.map(optionValue => (
                    <option key={optionValue} value={optionValue}>
                      {optionValue === 'left' ? '왼쪽' : 
                        optionValue === 'right' ? '오른쪽' : optionValue}
                    </option>
                  ))}
                </select>
              )}
              
              {option.type === 'number' && (
                <div className="d-flex align-items-center">
                  <input
                    type="range"
                    className="form-range me-2"
                    min={option.min || 1}
                    max={option.max || 10}
                    value={layoutProps[option.name] ?? option.default}
                    onChange={(e) => handlePropertyChange(option.name, parseInt(e.target.value))}
                  />
                  <span className="ms-2">{layoutProps[option.name] ?? option.default}</span>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
      
      {!selectedLayout && (
        <div className="no-layout-selected">
          <p className="text-muted">레이아웃을 선택해주세요</p>
        </div>
      )}

      {/* 스킨 구매 모달 */}
      <SkinPurchaseModal
        isOpen={isPurchaseModalOpen}
        onClose={() => setIsPurchaseModalOpen(false)}
        onPurchaseSuccess={handlePurchaseSuccess}
        skinType="LAYOUT"
      />
    </div>
  );
};

export default LayoutSelector; 